// ecom-ui.ts — the in-mission E-COM narrative overlay (domEcom). host.Ecom → Ecom() routes here
// INSIDE the story-modal queue (SP-paused + sequenced); on dismiss it unmounts + calls done()
// (releases the pause + shows the next queued modal). Lifted verbatim from main.ts's mountEcom so
// the shipped client AND the mission editor's Play host share ONE implementation (a sibling of
// command-ui.ts / hud-seams.ts / objectives-ui.ts).

import { EcomView, portraitFromArt } from './ecom-view.ts';
import { ecomPortrait } from './ecom-message.ts';
import type { EcomInfo } from '../../ui/ecom.ts';
import {
  extractAtlasArt,
  titleArtToDataUrl,
  extractButtonArt,
  type ButtonArt,
} from '../title-art.ts';
import type { TexAtlasMgr } from '../../gfx/texatlasmgr.ts';

export interface EcomUiDeps {
  /** the texture atlas mgr — sources the ecom backdrops (ecomsmall/largebkgd.png) + the character
   *  portraits + the 9-slice button frame. */
  tam: TexAtlasMgr;
  /** optional pre-extracted 9-slice button art; else extracted once via extractButtonArt(tam). */
  objBtnArt?: ButtonArt | null;
  /** play a GUI sfx on a button press, wired to the host SoundMgr (no-op headless). */
  playSfx: (sfx: number) => void;
  /** the element the overlay root mounts into (default document.body). The root is
   *  `position:fixed; inset:0`; a TRANSFORMED pane-covering host element bounds it to the
   *  pane instead of the window (the mission editor's play-in-place WYSIWYG mode). */
  host?: HTMLElement;
}

export interface EcomUi {
  /** the domEcom session opt (gameSessionOpts.domEcom) — Ecom() calls this with the resolved
   *  message; `done()` releases the story-modal pause + advances the queue. */
  domEcom: (info: EcomInfo, done: () => void) => void;
  /** idempotently unmount the E-Com if it's up (game-over / host close). */
  teardown(): void;
}

type Portrait = { url: string; cx: number; cy: number } | null;

/** Stand up the shared in-mission E-Com overlay for one play session. */
export function createEcomUI(deps: EcomUiDeps): EcomUi {
  const { tam, playSfx } = deps;
  const objBtnArt: ButtonArt | undefined = deps.objBtnArt ?? extractButtonArt(tam) ?? undefined;
  const ecomSmallBg = extractAtlasArt(tam, 'ecomsmallbkgd.png');
  const ecomLargeBg = extractAtlasArt(tam, 'ecomlargebkgd.png');
  // the 4 character portraits (LARGE panel only) — extract each ONCE, keyed by character index.
  const portraitCache = new Map<number, Portrait>();
  const portraitFor = (idx: number): Portrait => {
    if (portraitCache.has(idx)) return portraitCache.get(idx)!;
    const name = ecomPortrait(idx); // null ⇒ this character has no portrait
    const art = name === null ? null : extractAtlasArt(tam, name);
    const p = portraitFromArt(art, art === null ? null : titleArtToDataUrl(art));
    portraitCache.set(idx, p);
    return p;
  };

  const root = document.createElement('div');
  root.id = 'dom-ecom';
  root.style.display = 'none';
  (deps.host ?? document.body).appendChild(root);
  let view: EcomView | null = null;

  const unmount = (): void => {
    root.style.display = 'none';
    view?.destroy();
    view = null;
    root.replaceChildren();
  };

  const domEcom = (info: EcomInfo, done: () => void): void => {
    root.style.display = 'flex';
    const bgArt = info.large ? ecomLargeBg : ecomSmallBg;
    view = new EcomView({
      root,
      fromIdx: info.fromIdx,
      toIdx: info.toIdx,
      message: info.message,
      large: info.large,
      nBackground: info.nBackground,
      backdrop: bgArt !== null ? titleArtToDataUrl(bgArt) : undefined,
      // portraits show on the LARGE panel only (the SMALL bar has none).
      fromPortrait: info.large ? portraitFor(info.fromIdx) : null,
      toPortrait: info.large ? portraitFor(info.toIdx) : null,
      buttonArt: objBtnArt,
      playSfx: (sfx: number) => {
        playSfx(sfx);
      },
      // dismiss: unmount FIRST, then run done() (release the story-modal pause + show the next
      // queued modal) so a follow-on modal isn't behind the ecom.
      onClose: () => {
        unmount();
        done();
      },
    });
  };

  return { domEcom, teardown: unmount };
}
